import { Component } from '@theme/component';

/**
 * Search Block Component
 * Handles search input integration with the modal search.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element
 * @property {HTMLButtonElement} searchButton - The search button element
 *
 * @extends {Component<Refs>}
 */
class SearchBlockComponent extends Component {
  requiredRefs = ['searchInput', 'searchButton'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    // Listen for Enter key on the search input
    this.refs.searchInput.addEventListener('keydown', this.#handleKeyDown, { signal });

    // Listen for search modal dialog open events
    const searchModal = document.querySelector('#search-modal');
    if (searchModal) {
      searchModal.addEventListener('dialog:open', this.#handleModalOpen, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles keydown events on the search input.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#openSearchModal();
    }
  };

  /**
   * Handles the modal open event.
   * This fires when the button is clicked OR when Enter is pressed.
   */
  #handleModalOpen = () => {
    this.#transferSearchValue();
  };

  /**
   * Opens the search modal.
   */
  #openSearchModal = () => {
    const searchModal = document.querySelector('#search-modal');
    if (!searchModal) return;

    searchModal.showDialog();
  };

  /**
   * Transfers the search value from block input to modal input and triggers search.
   */
  #transferSearchValue = () => {
    const searchValue = this.refs.searchInput.value.trim();

    if (!searchValue) return;

    const searchModal = document.querySelector('#search-modal');
    if (!searchModal) return;

    // Wait for the modal to be fully rendered
    setTimeout(() => {
      const predictiveSearch = searchModal.querySelector('predictive-search-component');

      if (!predictiveSearch) {
        console.warn('Predictive search component not found in modal');
        return;
      }

      const modalInput = predictiveSearch.querySelector('input[type="search"]');

      if (!modalInput) {
        console.warn('Modal search input not found');
        return;
      }

      // Set the value in the modal's search input
      modalInput.value = searchValue;

      // Trigger the search by dispatching an input event
      const inputEvent = new InputEvent('input', {
        bubbles: true,
        cancelable: true,
        inputType: 'insertText',
      });

      modalInput.dispatchEvent(inputEvent);

      // Focus the modal input
      requestAnimationFrame(() => {
        modalInput.focus();
      });
    }, 100);
  };
}

customElements.define('search-block-component', SearchBlockComponent);
